import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { FileText, Workflow, CheckCircle2, Clock } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { cn } from "@/lib/utils";

interface Activity {
  id: string;
  type: "job" | "form" | "workflow" | "task";
  title: string;
  description: string;
  timestamp: string;
  status?: string;
  link?: string;
}

export function RecentActivity() {
  const navigate = useNavigate();
  const [activities, setActivities] = useState<Activity[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadRecentActivity();
  }, []);

  const loadRecentActivity = async () => {
    try {
      const { data: profile } = await supabase
        .from("profiles")
        .select("org_id")
        .eq("id", (await supabase.auth.getUser()).data.user?.id)
        .single();

      if (!profile?.org_id) return;

      // Get recent jobs
      const { data: jobs } = await supabase
        .from("jobs")
        .select("id, status, created_at")
        .eq("org_id", profile.org_id)
        .order("created_at", { ascending: false })
        .limit(3);

      // Get recent form submissions
      const { data: forms } = await supabase
        .from("form_submissions")
        .select(`
          id,
          status,
          created_at,
          form_template:form_templates(name),
          job:jobs(id)
        `)
        .eq("org_id", profile.org_id)
        .order("created_at", { ascending: false })
        .limit(3);

      // Get recent workflow completions
      const { data: workflows } = await supabase
        .from("workflow_instances")
        .select(`
          id,
          status,
          completed_at,
          template:workflow_templates(name),
          job:jobs(id)
        `)
        .eq("org_id", profile.org_id)
        .order("completed_at", { ascending: false })
        .limit(3);

      const combinedActivities: Activity[] = [
        ...(jobs || []).map((job) => ({
          id: job.id,
          type: "job" as const,
          title: `Job Updated`,
          description: `Status: ${job.status}`,
          timestamp: job.created_at,
          status: job.status,
          link: `/jobs/${job.id}`,
        })),
        ...(forms || []).map((form: any) => ({
          id: form.id,
          type: "form" as const,
          title: `Form: ${form.form_template?.name || "Unknown"}`,
          description: `Status: ${form.status}`,
          timestamp: form.created_at,
          status: form.status,
          link: form.job?.id ? `/jobs/${form.job.id}` : undefined,
        })),
        ...(workflows || []).map((workflow: any) => ({
          id: workflow.id,
          type: "workflow" as const,
          title: `Workflow: ${workflow.template?.name || "Unknown"}`,
          description: `Status: ${workflow.status}`,
          timestamp: workflow.completed_at,
          status: workflow.status,
          link: workflow.job?.id ? `/jobs/${workflow.job.id}` : undefined,
        })),
      ];

      // Sort by timestamp and take top 10
      combinedActivities.sort(
        (a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
      );

      setActivities(combinedActivities.slice(0, 10));
    } catch (error: any) {
      console.error("Error loading recent activity:", error);
    } finally {
      setLoading(false);
    }
  };

  const getIcon = (type: string) => {
    switch (type) {
      case "workflow":
        return <Workflow className="h-4 w-4" />;
      case "form":
        return <FileText className="h-4 w-4" />;
      case "task":
        return <CheckCircle2 className="h-4 w-4" />;
      default:
        return <Clock className="h-4 w-4" />;
    }
  };

  const getStatusColor = (status?: string) => {
    if (!status) return "secondary";
    if (status.toLowerCase().includes("complete")) return "default";
    if (status.toLowerCase().includes("progress")) return "secondary";
    return "outline";
  };

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Recent Activity</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">Loading...</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Recent Activity</CardTitle>
        <CardDescription>Latest updates across your organization</CardDescription>
      </CardHeader>
      <CardContent>
        {activities.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-8">
            No recent activity
          </p>
        ) : (
          <div className="space-y-3">
            {activities.map((activity) => (
              <div
                key={activity.id}
                className={cn(
                  "flex items-start gap-3 p-3 rounded-lg border",
                  activity.link && "cursor-pointer hover:bg-muted/50 transition-colors"
                )}
                onClick={() => activity.link && navigate(activity.link)}
              >
                <div className="p-2 rounded-lg bg-primary/10">
                  {getIcon(activity.type)}
                </div>
                <div className="flex-1 space-y-1">
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-medium">{activity.title}</p>
                    {activity.status && (
                      <Badge variant={getStatusColor(activity.status)} className="text-xs">
                        {activity.status}
                      </Badge>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {activity.description}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {new Date(activity.timestamp).toLocaleString()}
                  </p>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
